<!-- eslint-disable -->
<template>
  <div class="pa-4">
    <!-- Actions -->
    <div class="d-flex justify-end mb-4">
      <v-btn color="primary" @click="openCreateDialog">
        <v-icon start>mdi-plus</v-icon>
        {{ $t('admin.createPage') }}
      </v-btn>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <!-- Pages Table -->
    <v-table v-else>
      <thead>
        <tr>
          <th>{{ $t('admin.slug') }}</th>
          <th>{{ $t('admin.locale') }}</th>
          <th>{{ $t('admin.pageTitle') }}</th>
          <th>{{ $t('admin.status') }}</th>
          <th>{{ $t('admin.lastUpdated') }}</th>
          <th>{{ $t('admin.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="page in pages" :key="page.pageId">
          <td>
            <code>{{ page.slug }}</code>
          </td>
          <td>
            <v-chip size="small" :color="page.locale === 'da' ? 'primary' : 'info'">
              {{ page.locale.toUpperCase() }}
            </v-chip>
          </td>
          <td>{{ page.title }}</td>
          <td>
            <v-chip
              :color="page.isPublished ? 'success' : 'grey'"
              size="small"
            >
              {{ page.isPublished ? $t('admin.published') : $t('admin.draft') }}
            </v-chip>
          </td>
          <td>{{ formatDate(page.updated_at) }}</td>
          <td>
            <v-btn
              icon
              variant="text"
              size="small"
              @click="editPage(page)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              :href="`/page/${page.slug}`"
              target="_blank"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              color="error"
              @click="confirmDelete(page)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-table>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="800" persistent>
      <v-card>
        <v-card-title>
          {{ editingPage ? $t('admin.editPage') : $t('admin.createPage') }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="pageForm.slug"
                label="Slug"
                variant="outlined"
                :disabled="!!editingPage"
                hint="e.g., about, terms, privacy"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="pageForm.locale"
                :items="localeOptions"
                label="Locale"
                variant="outlined"
                :disabled="!!editingPage"
              ></v-select>
            </v-col>
          </v-row>
          <v-text-field
            v-model="pageForm.title"
            :label="$t('admin.pageTitle')"
            variant="outlined"
            class="mb-4"
          ></v-text-field>
          
          <!-- Rich Text Editor -->
          <div class="editor-wrapper mb-4">
            <label class="text-caption text-grey mb-1 d-block">{{ $t('admin.content') }}</label>
            <div class="editor-toolbar">
              <v-btn-group density="compact" variant="outlined">
                <v-btn size="small" @click="editor?.chain().focus().toggleBold().run()" :class="{ 'bg-primary': editor?.isActive('bold') }">
                  <v-icon size="small">mdi-format-bold</v-icon>
                </v-btn>
                <v-btn size="small" @click="editor?.chain().focus().toggleItalic().run()" :class="{ 'bg-primary': editor?.isActive('italic') }">
                  <v-icon size="small">mdi-format-italic</v-icon>
                </v-btn>
                <v-btn size="small" @click="editor?.chain().focus().toggleUnderline().run()" :class="{ 'bg-primary': editor?.isActive('underline') }">
                  <v-icon size="small">mdi-format-underline</v-icon>
                </v-btn>
              </v-btn-group>
              
              <v-btn-group density="compact" variant="outlined" class="ml-2">
                <v-btn size="small" @click="editor?.chain().focus().toggleHeading({ level: 2 }).run()" :class="{ 'bg-primary': editor?.isActive('heading', { level: 2 }) }">
                  <v-icon size="small">mdi-format-header-2</v-icon>
                </v-btn>
                <v-btn size="small" @click="editor?.chain().focus().toggleHeading({ level: 3 }).run()" :class="{ 'bg-primary': editor?.isActive('heading', { level: 3 }) }">
                  <v-icon size="small">mdi-format-header-3</v-icon>
                </v-btn>
              </v-btn-group>
              
              <v-btn-group density="compact" variant="outlined" class="ml-2">
                <v-btn size="small" @click="editor?.chain().focus().toggleBulletList().run()" :class="{ 'bg-primary': editor?.isActive('bulletList') }">
                  <v-icon size="small">mdi-format-list-bulleted</v-icon>
                </v-btn>
                <v-btn size="small" @click="editor?.chain().focus().toggleOrderedList().run()" :class="{ 'bg-primary': editor?.isActive('orderedList') }">
                  <v-icon size="small">mdi-format-list-numbered</v-icon>
                </v-btn>
              </v-btn-group>
              
              <v-btn-group density="compact" variant="outlined" class="ml-2">
                <v-btn size="small" @click="setLink">
                  <v-icon size="small">mdi-link</v-icon>
                </v-btn>
                <v-btn size="small" @click="editor?.chain().focus().unsetLink().run()" :disabled="!editor?.isActive('link')">
                  <v-icon size="small">mdi-link-off</v-icon>
                </v-btn>
              </v-btn-group>
              
              <v-btn-group density="compact" variant="outlined" class="ml-2">
                <v-btn size="small" @click="editor?.chain().focus().setHorizontalRule().run()">
                  <v-icon size="small">mdi-minus</v-icon>
                </v-btn>
              </v-btn-group>
            </div>
            <editor-content :editor="editor" class="editor-content" />
          </div>
          <v-text-field
            v-model="pageForm.metaDescription"
            :label="$t('admin.metaDescription')"
            variant="outlined"
            counter="160"
            class="mb-4"
          ></v-text-field>
          <v-switch
            v-model="pageForm.isPublished"
            :label="$t('admin.published')"
            color="primary"
          ></v-switch>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="savePage" :loading="saving">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-error">
          {{ $t('admin.deletePage') }}
        </v-card-title>
        <v-card-text>
          {{ $t('admin.deletePageConfirm', { title: pageToDelete?.title }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" @click="deletePage">{{ $t('common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, reactive, onMounted, onBeforeUnmount, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import api from '../../services/api'

const { locale } = useI18n()

const pages = ref([])
const loading = ref(false)
const saving = ref(false)

const showDialog = ref(false)
const editingPage = ref(null)
const pageForm = reactive({
  slug: '',
  locale: 'da',
  title: '',
  content: '',
  metaDescription: '',
  isPublished: true
})

const showDeleteDialog = ref(false)
const pageToDelete = ref(null)

const localeOptions = [
  { value: 'da', title: 'Dansk (DA)' },
  { value: 'en', title: 'English (EN)' }
]

// TipTap Editor
const editor = useEditor({
  extensions: [
    StarterKit,
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer'
      }
    })
  ],
  content: '',
  onUpdate: ({ editor }) => {
    pageForm.content = editor.getHTML()
  }
})

const setLink = () => {
  const previousUrl = editor.value?.getAttributes('link').href
  const url = window.prompt('URL', previousUrl)
  
  if (url === null) return
  
  if (url === '') {
    editor.value?.chain().focus().extendMarkRange('link').unsetLink().run()
    return
  }
  
  editor.value?.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

// Watch for dialog open to set editor content
watch(showDialog, (isOpen) => {
  if (isOpen && editor.value) {
    editor.value.commands.setContent(pageForm.content || '')
  }
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})

const loadPages = async () => {
  loading.value = true
  try {
    const response = await api.get('/admin/pages')
    pages.value = response.data.pages || []
  } catch (error) {
    console.error('Failed to load pages:', error)
  } finally {
    loading.value = false
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString(locale.value)
}

const openCreateDialog = () => {
  editingPage.value = null
  Object.assign(pageForm, {
    slug: '',
    locale: 'da',
    title: '',
    content: '',
    metaDescription: '',
    isPublished: true
  })
  showDialog.value = true
}

const editPage = async (page) => {
  editingPage.value = page
  
  // Fetch full content
  try {
    const response = await api.get('/pages', {
      params: { slug: page.slug, locale: page.locale }
    })
    const fullPage = response.data.page
    
    Object.assign(pageForm, {
      slug: fullPage.slug,
      locale: fullPage.locale,
      title: fullPage.title,
      content: fullPage.content,
      metaDescription: fullPage.metaDescription || '',
      isPublished: !!page.isPublished
    })
    showDialog.value = true
  } catch (error) {
    console.error('Failed to load page content:', error)
  }
}

const closeDialog = () => {
  showDialog.value = false
  editingPage.value = null
}

const savePage = async () => {
  saving.value = true
  try {
    if (editingPage.value) {
      await api.put('/admin/pages', {
        pageId: editingPage.value.pageId,
        title: pageForm.title,
        content: pageForm.content,
        metaDescription: pageForm.metaDescription,
        isPublished: pageForm.isPublished
      })
    } else {
      await api.post('/admin/pages', pageForm)
    }
    
    closeDialog()
    loadPages()
  } catch (error) {
    console.error('Failed to save page:', error)
    alert(error.response?.data?.error || 'Failed to save')
  } finally {
    saving.value = false
  }
}

const confirmDelete = (page) => {
  pageToDelete.value = page
  showDeleteDialog.value = true
}

const deletePage = async () => {
  if (!pageToDelete.value) return
  
  try {
    await api.delete('/admin/pages', {
      data: { pageId: pageToDelete.value.pageId }
    })
    
    pages.value = pages.value.filter(p => p.pageId !== pageToDelete.value.pageId)
    showDeleteDialog.value = false
    pageToDelete.value = null
  } catch (error) {
    console.error('Failed to delete page:', error)
    alert(error.response?.data?.error || 'Failed to delete')
  }
}

onMounted(() => {
  loadPages()
})
</script>

<style scoped>
.editor-wrapper {
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  overflow: hidden;
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
  background: #f5f5f5;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.editor-content {
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
  padding: 16px;
}

.editor-content :deep(.ProseMirror) {
  min-height: 268px;
  outline: none;
}

.editor-content :deep(.ProseMirror p) {
  margin-bottom: 0.75em;
}

.editor-content :deep(.ProseMirror h2) {
  font-size: 1.5em;
  font-weight: bold;
  margin-top: 1em;
  margin-bottom: 0.5em;
}

.editor-content :deep(.ProseMirror h3) {
  font-size: 1.25em;
  font-weight: bold;
  margin-top: 1em;
  margin-bottom: 0.5em;
}

.editor-content :deep(.ProseMirror ul),
.editor-content :deep(.ProseMirror ol) {
  padding-left: 1.5em;
  margin-bottom: 0.75em;
}

.editor-content :deep(.ProseMirror a) {
  color: #1976d2;
  text-decoration: underline;
}

.editor-content :deep(.ProseMirror hr) {
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin: 1em 0;
}
</style>